import { useMemo } from 'react';
import classNames from 'classnames';

import useBreakpoint from '@hooks/useBreakpoint';

import { TimelineStep } from '@customTypes/timeline';
import TimelineLabel from '../TimelineLabel/TimelineLabel';
import TimelineCluster from '../TimelineCluster/TimelineCluster';

import './Timeline.scss';

interface TimelineProps {
  steps: TimelineStep[];
  className?: string;
}

/**
 * Calculates track boundaries (from first dot center to last dot center)
 * @param numGroups - Total number of groups
 * @returns Object with trackStart and trackWidth
 */
function calculateTrackBounds(numGroups: number) {
  if (numGroups === 0) {
    return { trackStart: 0, trackWidth: 0 };
  }

  // Calculate track boundaries: from first dot center to last dot center
  const columnWidth = 100 / numGroups;
  const trackStart = columnWidth / 2; // Center of first column
  const trackEnd = 100 - columnWidth / 2; // Center of last column
  const trackWidth = numGroups > 1 ? trackEnd - trackStart : 0;

  return { trackStart, trackWidth };
}

export default function Timeline({ steps, className }: TimelineProps) {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  const tooltipPlacement = isMobile ? 'right' : 'top';

  const { groups, gridTemplateColumns, trackStart, trackWidth, lastGroupWithProgressIndex } = useMemo(() => {
    const numGroups = steps.length;
    const groups = steps;

    // Calculate grid columns for even spacing
    // Use equal-width columns so labels and dots are evenly distributed
    const gridCols = numGroups > 0 ? `repeat(${numGroups}, 1fr)` : '1fr';

    // Calculate track boundaries (for background track only)
    const { trackStart, trackWidth } = calculateTrackBounds(numGroups);

    // Find the last cluster with any progress (completed or in-progress) - return index instead of group
    const lastGroupWithProgressIndex = (() => {
      for (let i = groups.length - 1; i >= 0; i -= 1) {
        const status = groups[i].status;
        if (status === 'complete' || status === 'in-progress') {
          return i;
        }
      }
      return null;
    })();

    return {
      groups,
      gridTemplateColumns: gridCols,
      trackStart,
      trackWidth,
      lastGroupWithProgressIndex,
    };
  }, [steps]);

  return (
    <div className={classNames('timeline', className)}>
      <div className='timeline__labels-container' style={{ gridTemplateColumns }}>
        {groups.map((group, index) => (
          <TimelineLabel key={`label-${index}-${group.id}`} group={group} />
        ))}
      </div>

      <div className='timeline__track-container' style={{ gridTemplateColumns }}>
        {/* Global background track - constrained between first and last dots */}
        <div className='timeline__line' style={{ left: `${trackStart}%`, width: `${trackWidth}%` }} />

        {groups.map((group, index) => (
          <TimelineCluster
            key={`cluster-${index}-${group.id}`}
            group={group}
            index={index}
            groups={groups}
            lastProgressIndex={lastGroupWithProgressIndex}
            tooltipPlacement={tooltipPlacement}
          />
        ))}
      </div>

      <div className='timeline__dates-container' style={{ gridTemplateColumns }}>
        {groups.map((group, index) => (
          <div
            key={`date-${index}-${group.id}`}
            className={classNames('timeline__date-cell', {
              'timeline__date-cell--completed': group.status === 'complete',
            })}
          >
            {group.dateText}
          </div>
        ))}
      </div>
    </div>
  );
}
